"use client";

import { useDeferredValue, useEffect, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  EXAMPLE_PNG_BASE64,
  type ImageStrings,
} from "@/components/tools/image-to-base64";
import { formatSize } from "@/components/tools/latex-dev/image-encode";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadBlob } from "@/lib/tool-kit/download";
import {
  fileNameFor,
  measureImage,
} from "@/lib/tool-kit/latex-dev/image-browser";
import {
  type ParsedImageInput,
  parseImageInput,
} from "@/lib/tool-kit/latex-dev/image-data";

export function ImageDecodePanel({ strings }: { strings: ImageStrings }) {
  const [source, setSource] = useState("");
  const deferred = useDeferredValue(source);
  const result = deferred.trim() === "" ? null : parseImageInput(deferred);

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.decodeHint}
        id="itb-decode"
        label={strings.decodeLabel}
        onChange={setSource}
        placeholder={strings.decodePlaceholder}
        rows={8}
        value={source}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            setSource(`data:image/png;base64,${EXAMPLE_PNG_BASE64}`)
          }
          type="button"
        >
          {strings.decodeExampleLabel}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div aria-live="polite" className="space-y-4">
        {result === null ? <Notice>{strings.emptyDecode}</Notice> : null}
        {result?.ok === false ? (
          <ErrorNotice>{strings.decodeErrors[result.code]}</ErrorNotice>
        ) : null}
        {result?.ok ? <Decoded image={result.image} strings={strings} /> : null}
      </div>
    </div>
  );
}

function Decoded({
  image,
  strings,
}: {
  image: ParsedImageInput;
  strings: ImageStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null
  );
  const isImage = image.mime.startsWith("image/");
  const dataUri = `data:${image.mime};base64,${image.base64}`;
  useEffect(() => {
    let current = true;
    setSize(null);
    if (isImage) {
      measureImage(dataUri).then((measured) => {
        if (current) {
          setSize(measured);
        }
      });
    }
    return () => {
      current = false;
    };
  }, [dataUri, isImage]);
  const warnings = image.warnings.map((warning) =>
    t(strings.decodeWarnings[warning], {
      declared: image.declaredMime || strings.none,
      actual: image.sniffedMime ?? strings.none,
      type: image.mime,
    })
  );
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      {isImage ? (
        <>
          {/* biome-ignore lint/performance/noImgElement: a data URI cannot use next/image */}
          {/* biome-ignore lint/correctness/useImageSize: the natural size is only known once the image has loaded; CSS bounds it */}
          <img
            alt={strings.previewAlt}
            className="max-h-64 max-w-full rounded border border-border bg-card p-2"
            src={dataUri}
          />
        </>
      ) : null}
      <DataTable
        head={[strings.typeLabel, strings.fileSize, strings.dimensions]}
        rows={[
          [
            image.mime,
            formatSize(image.bytes.length, locale),
            size && size.width > 0
              ? t(strings.dimensionsValue, {
                  width: fmt(size.width),
                  height: fmt(size.height),
                })
              : strings.unknownDimensions,
          ],
        ]}
      />
      <DataTable
        head={[strings.detectedType, strings.declaredType, strings.foundIn]}
        rows={[
          [
            image.sniffedMime ?? strings.none,
            image.declaredMime || strings.none,
            strings.sources[image.source],
          ],
        ]}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            downloadBlob(
              fileNameFor(image.mime),
              new Blob([image.bytes as BlobPart], { type: image.mime })
            )
          }
          type="button"
        >
          {strings.downloadImage}
        </button>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyDataUri}
          text={dataUri}
        />
      </div>
      {warnings.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.warningsHeading}</h3>
          <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}
